import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useCreateHomework } from '@/services/homeworks';
import { useTimetable } from '@/services/children';
import { GlobalHeader } from '@/components/GlobalHeader';
import { BottomTabBar } from '@/components/ui/BottomTabBar';
import type { HomeworkExercise } from '@/types/api';

type Step = 'form' | 'review';

export function AddHomeworkPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { childId } = useAuthStore();
  const { data: timetableData } = useTimetable(childId ?? '');
  const { mutateAsync: createHomework, isPending } = useCreateHomework(childId ?? '');

  const subjects = Array.from(
    new Set(timetableData?.entries.map((e) => e.subject) ?? []),
  );

  const [subject, setSubject] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [themeInput, setThemeInput] = useState('');
  const [themes, setThemes] = useState<string[]>([]);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf'>('image/jpeg');
  const [fileName, setFileName] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('form');
  const [previewExercises, setPreviewExercises] = useState<HomeworkExercise[]>([]);
  const [homeworkId, setHomeworkId] = useState<string | null>(null);
  const [error, setError] = useState('');

  // Camera capture (direct, for mobile scan)
  const cameraRef = useRef<HTMLInputElement>(null);
  // File picker (gallery / filesystem, supports PDF)
  const fileRef = useRef<HTMLInputElement>(null);

  const activeSubject = subject === '__custom__' ? customSubject : subject;

  const addTheme = () => {
    const trimmed = themeInput.trim();
    if (trimmed && !themes.includes(trimmed)) setThemes((prev) => [...prev, trimmed]);
    setThemeInput('');
  };

  const removeTheme = (t: string) => setThemes((prev) => prev.filter((x) => x !== t));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const mime = file.type as 'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf';
    if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(mime)) {
      setError(t('homework.supported_formats'));
      return;
    }
    setError('');
    setMediaType(mime);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setImageBase64(result.split(',')[1] ?? '');
      setPreviewUrl(mime !== 'application/pdf' ? result : null);
    };
    reader.readAsDataURL(file);
    // Reset so the same file can be re-selected after clearing
    e.target.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!activeSubject.trim()) {
      setError(t('homework.subject_required'));
      return;
    }
    try {
      const result = await createHomework({
        subject: activeSubject.trim(),
        themes,
        ...(imageBase64 ? { image_base64: imageBase64, media_type: mediaType } : {}),
      });
      setPreviewExercises(result.exercises);
      setHomeworkId(result.homework_id);
      setStep('review');
    } catch {
      setError(t('homework.create_failed'));
    }
  };

  // ── Review step ───────────────────────────────────────────────────────────────
  if (step === 'review') {
    return (
      <div className="page-center">
        <div className="phone-mockup">
          <GlobalHeader showBack onBack={() => setStep('form')} customTitle={t('homework.review_title')} />
          <div className="app-content" style={{ paddingBottom: '24px' }}>

            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '0 0 16px' }}>
              {t('homework.extracted_count', { count: previewExercises.length })}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              {previewExercises.map((ex, i) => (
                <div key={ex.exercise_id} className="card">
                  <p style={{ margin: '0 0 6px', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('homework.exercise')} {i + 1}
                  </p>
                  <p style={{ margin: 0, color: 'var(--text-dark)', fontSize: '0.875rem', lineHeight: 1.5 }}>
                    {ex.prompt}
                  </p>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="btn btn-primary"
              style={{ width: '100%' }}
              onClick={() => navigate(`/student/homework/${homeworkId}`)}
            >
              {t('homework.start_working')}
            </button>

          </div>
          <BottomTabBar activeTab="Home" />
        </div>
      </div>
    );
  }

  // ── Form step ─────────────────────────────────────────────────────────────────
  const fileSelected = !!imageBase64;

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute="/student/homework" />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 20px' }}>
            {t('homework.add_hw_title')}
          </h1>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Subject */}
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                {t('homework.subject')} *
              </label>
              {subjects.length > 0 ? (
                <select
                  className="input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                >
                  <option value="">{t('homework.select_subject')}…</option>
                  {subjects.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                  <option value="__custom__">{t('homework.other_option')}</option>
                </select>
              ) : (
                <input
                  type="text"
                  className="input"
                  placeholder={t('homework.subject_placeholder')}
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                />
              )}
              {subject === '__custom__' && (
                <input
                  type="text"
                  className="input"
                  placeholder={t('homework.enter_subject_name')}
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  style={{ marginTop: '8px' }}
                />
              )}
            </div>

            {/* Themes */}
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                {t('homework.topics')}
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="input"
                  placeholder={t('homework.topic_placeholder')}
                  value={themeInput}
                  onChange={(e) => setThemeInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTheme(); } }}
                  style={{ flex: 1, marginBottom: 0 }}
                />
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={addTheme}
                  style={{ width: 'auto', padding: '0 16px', height: '48px', flexShrink: 0 }}
                >
                  {t('homework.add_theme')}
                </button>
              </div>
              {themes.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                  {themes.map((theme) => (
                    <span
                      key={theme}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        fontSize: '0.75rem', padding: '3px 10px', borderRadius: '999px',
                        background: 'var(--primary-light)', color: 'var(--primary)', fontWeight: 500,
                      }}
                    >
                      {theme}
                      <button
                        type="button"
                        onClick={() => removeTheme(theme)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--primary)', fontSize: '1rem', lineHeight: 1, padding: 0, marginLeft: '2px',
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Document: scan + upload */}
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '8px' }}>
                {t('homework.image_label')}{' '}
                <span style={{ fontWeight: 400 }}>{t('homework.image_optional')}</span>
              </label>

              {/* Hidden inputs */}
              <input
                ref={cameraRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              {/* Two side-by-side buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                {[
                  { label: t('homework.scan_btn'), ref: cameraRef },
                  { label: t('homework.upload_btn'), ref: fileRef },
                ].map(({ label, ref: btnRef }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => btnRef.current?.click()}
                    style={{
                      flex: 1,
                      border: `2px dashed ${fileSelected ? 'var(--primary)' : 'var(--border)'}`,
                      borderRadius: '12px',
                      padding: '18px 8px',
                      background: fileSelected ? 'var(--primary-light)' : 'white',
                      color: fileSelected ? 'var(--primary)' : 'var(--text-muted)',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      fontWeight: 600,
                      textAlign: 'center',
                      transition: 'all 0.15s',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* File feedback */}
              {fileName && (
                <p style={{ fontSize: '0.8rem', color: 'var(--primary)', margin: '8px 0 0', fontWeight: 500 }}>
                  ✓ {fileName}
                </p>
              )}
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt={t('homework.preview_alt')}
                  style={{
                    maxHeight: '120px', width: '100%', borderRadius: '8px',
                    marginTop: '8px', objectFit: 'cover',
                  }}
                />
              )}
            </div>

            {error && (
              <p style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: 0 }}>
                ⚠️ {error}
              </p>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={isPending}
            >
              {isPending ? t('actions.creating') : `📚 ${t('homework.create_btn')}`}
            </button>

          </form>
        </div>
        <BottomTabBar activeTab="Home" />
      </div>
    </div>
  );
}
